import { useFormContext } from "react-hook-form";
import { connect } from "react-redux";
import { RootState } from "../../store/root-reducer";
import { classNames } from "../../helper";
import { PiCheckBold } from "react-icons/pi";
import { useTranslation } from "react-i18next";

function VerifyContactInfo({ customerInfo }) {
  const { register, watch } = useFormContext();
  const { t } = useTranslation();
  const isVerified = watch("customerInfo.isVerify");

  const requiredInfoIsMissing =
    !customerInfo?.email ||
    !customerInfo?.phone ||
    !customerInfo?.address1 ||
    !customerInfo?.city ||
    !customerInfo?.state ||
    !customerInfo?.zip;

  const InfoRow = ({
    label,
    value,
    isRequired,
  }: {
    label: string;
    value: string | null;
    isRequired?: boolean;
  }) => {
    const isMissing = isRequired && !value;

    return (
      <div className="py-3 sm:grid sm:grid-cols-3 sm:gap-4">
        <dt className="text-sm font-medium text-gray-500">{label}</dt>
        <dd className="mt-1 text-base sm:col-span-2 sm:mt-0">
          {isMissing ? (
            <span className="font-semibold text-orange-600">
              {t("Missing - Please update this field")}
            </span>
          ) : (
            value || "-"
          )}
        </dd>
      </div>
    );
  };

  const fullAddress = [
    customerInfo?.address1,
    customerInfo?.address2,
    `${customerInfo?.city || ""}, ${customerInfo?.state || ""} ${
      customerInfo?.zip || ""
    }`,
  ]
    .filter(Boolean)
    .join("\n");

  return (
    <div className="mb-8">
      <p className="mt-1 text-sm text-gray-600">
        {t(
          "Review the information below. If it's incorrect, please update it."
        )}
      </p>

      <div className="mt-4 space-y-3 rounded-lg border border-gray-200 bg-white p-4">
        <dl className="divide-y divide-gray-200">
          <InfoRow label={t("Email")} value={customerInfo?.email} isRequired />
          <InfoRow
            label={t("Phone number")}
            value={customerInfo?.phone}
            isRequired
          />

          <div className="py-3 sm:grid sm:grid-cols-3 sm:gap-4">
            <dt className="text-sm font-medium text-gray-500">
              {t("Furniture Location Address")}
            </dt>
            <dd className="mt-1 whitespace-pre-line text-base sm:col-span-2 sm:mt-0">
              {!customerInfo?.address1 || !customerInfo?.city ? (
                <span className="font-semibold text-orange-600">
                  {t("Missing - Please update this field")}
                </span>
              ) : (
                fullAddress
              )}
            </dd>
          </div>

          <InfoRow
            label={t("SMS Updates")}
            value={
              customerInfo?.allowSMS ? t("Yes, subscribed") : t("No, opted out")
            }
          />
        </dl>
      </div>

      <div className="mt-6 rounded-md border border-gray-300 bg-white p-4">
        <label
          className={classNames(
            "flex cursor-pointer flex-row select-none items-center",
            requiredInfoIsMissing && "cursor-not-allowed opacity-60"
          )}
        >
          <input
            type="checkbox"
            className="hidden"
            checked={isVerified}
            disabled={requiredInfoIsMissing}
            {...register("customerInfo.isVerify", {
              required: true,
            })}
          />
          <div
            className={classNames(
              isVerified
                ? "border-[#2D6CCA] bg-[#2D6CCA]"
                : " border-gray-400 bg-white",
              "h-5 w-5 shrink-0 rounded border flex items-center justify-center transition-all",
              requiredInfoIsMissing && "bg-gray-200 border-gray-300"
            )}
            aria-hidden="true"
          >
            {isVerified && <PiCheckBold className="size-4 text-white" />}
          </div>
          <span className="text-sm text-gray-800 tracking-tight ms-3">
            {t("I verify that the information provided is correct.")}
            <span className="text-red-600 ms-1 font-semibold">*</span>
          </span>
        </label>
        {requiredInfoIsMissing && (
          <p className="text-xs text-orange-600 mt-2 pl-8">
            {t("You must provide all missing details before you can verify.")}
          </p>
        )}
      </div>
    </div>
  );
}

const mapStateToProps = (state: RootState) => ({
  customerInfo: state?.ace2Claim?.customer_data || {},
});

export default connect(mapStateToProps)(VerifyContactInfo);
